import React, { useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ClientSidebarManager, CollapsedWidth, ExpandedWidth } from './ClientSidebarManager';

export const SIDEBAR_CONTENT_ID = 'newron-sidebar-content';

type SidebarOptions = {
  /**
   * The key to use for storing the state in session storage. Defaults to 'default'.
   * All components using the same key will share the same state.
   */
  key?: string;
  /**
   * The width of the sidebar in expanded state. Defaults to '300px'.
   */
  expandedWidth?: ExpandedWidth;
  /**
   * The width of the sidebar in closed (collapsed) state. Defaults to '0'.
   * To render it as a small sidebar, set it to '48px'.
   */
  collapsedWidth?: CollapsedWidth;
  /**
   * The initial state (expanded/collapsed) of the sidebar. Defaults to false (collapsed).
   * This value is only used on first render, as long as the state is not persisted in session storage.
   * Subsequent renders of the component will use the state from session storage.
   */
  expanded?: boolean;
};

type ReturnType = {
  expanded: boolean;
  /**
   * Opens (expands) the sidebar and persists the state in session storage.
   */
  openSidebar: () => void;
  /**
   * Closes (collapses) the sidebar and persists the state in session storage.
   */
  closeSidebar: () => void;
  /**
   * Opens/closes the sidebar and persists the state in session storage.
   */
  toggleSidebar: () => void;
  /**
   * Hides the sidebar. Does NOT persist the state in session storage.
   */
  hideSidebar: () => void;
};

/*
The current sidebar implementation comes with a few limitations:

  - There’s no explicit link between `useSidebar` calls and the actual sidebar content component.
  - Any component that uses `useSidebar` can manipulate the sidebar state, even if it doesn’t render any content.
    (This could be seen as a feature, but it introduces ambiguity and risk.)
  - The `Content` component is generic and rendered via a portal, which allows multiple instances to mount simultaneously,
    potentially breaking the visual layout of the sidebar.
  - Width properties (`expandedWidth`, `collapsedWidth`) are passed via `useSidebar` instead of being tied to `Content`,
    making it harder to ensure consistent dimensions where needed.

These issues have led to some practical problems:

  1. In scenarios requiring multiple sidebars, we’ve had to manually unmount one and mount another from the consumer side to maintain correct behavior.
  2. Components that use `useSidebar` assume a specific sidebar context, which isn't guaranteed. This leads to unexpected behavior when such components try to update sidebar state.

We've explored a couple of potential improvements, but none have fully solved the problem:

  - **Lock via `useSidebar`**: A locking mechanism could enforce that only one consumer has control over the sidebar.
    The first `useSidebar` to mount would acquire the lock, and others would be blocked. While this enforces ownership, it limits flexibility.
    Additionally, when the lock-holding component unmounts, other mounted instances will immediately try to take over, leading to unpredictable behavior.

  - **Lock via `Content` key**: `Content` could accept a `key` prop, and only `useSidebar` instances providing the same key would be allowed to control the sidebar.
    However, since multiple `Content` components can still be mounted, unmounting the one with the lock causes a race condition where others compete for control.
    Without knowledge of their context, there's no reliable way to determine which one should win control.

*/

function useSidebar(options?: SidebarOptions): ReturnType {
  const [expandedState, setExpandedState] = React.useState<boolean>(options?.expanded ?? false);
  const expandedWidth = options?.expandedWidth ?? '300px';
  const collapsedWidth = options?.collapsedWidth ?? '0';

  const key = options?.key ?? 'default';

  const listener = (state: [expanded: boolean, expandedWidth: ExpandedWidth, collapsedWidth: CollapsedWidth]) => {
    // not overwriting collapsedWidth because it should not change for this instance
    const [expanded] = state;
    setExpandedState(expanded);
  };

  React.useEffect(() => {
    ClientSidebarManager.getInstance().init(key, { expanded: options?.expanded, expandedWidth, collapsedWidth });
    ClientSidebarManager.getInstance().subscribe(listener);

    return () => {
      ClientSidebarManager.getInstance().unsubscribe(listener);
    };
  }, [key, options?.expanded, expandedWidth, collapsedWidth]);

  const openSidebar = useCallback(() => {
    ClientSidebarManager.getInstance().setExpanded(true);
    document.getElementById(SIDEBAR_CONTENT_ID)?.focus();
  }, []);

  const closeSidebar = useCallback(() => {
    ClientSidebarManager.getInstance().setExpanded(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    ClientSidebarManager.getInstance().toggleExpanded();
  }, []);

  const hideSidebar = useCallback(() => {
    ClientSidebarManager.getInstance().hide();
  }, []);

  return {
    expanded: expandedState,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    hideSidebar,
  };
}

function useSidebarRenderer() {
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [expandedWidth, setExpandedWidth] = React.useState<ExpandedWidth>('300px');
  const [collapsedWidth, setCollapsedWidth] = React.useState<CollapsedWidth>('0');

  React.useEffect(() => {
    ClientSidebarManager.getInstance().subscribe(listener);

    return () => {
      ClientSidebarManager.getInstance().unsubscribe(listener);
    };
  }, []);

  const listener = (state: [expanded: boolean, expandedWidth: ExpandedWidth, collapsedWidth: CollapsedWidth]) => {
    const [expanded, expandedWidth, collapsedWidth] = state;
    setExpanded(expanded);
    setExpandedWidth(expandedWidth);
    setCollapsedWidth(collapsedWidth);
  };

  return {
    expanded,
    expandedWidth,
    collapsedWidth,
  };
}

type Props = {
  children: React.ReactNode;
};

function Content({ children }: Props): React.ReactPortal | null {
  const { expanded, collapsedWidth } = useSidebarRenderer();

  if (!expanded && collapsedWidth === '0') {
    return null;
  }

  const el = document.querySelector(`#${SIDEBAR_CONTENT_ID}`);
  if (!el) {
    console.warn('Could not find newron-sidebar-content in the document');
    return null;
  }

  return ReactDOM.createPortal(children, el);
}

export const ClientSidebar = {
  useSidebar,
  useSidebarRenderer,
  Content,
  sidebarContentId: SIDEBAR_CONTENT_ID,
};
